import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    LinearProgress,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Skeleton,
    Stack,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { CalendarPicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import useSubscriberLists from '../../hooks/useSubscriberLists';
import { UserContext } from '../../store/UserContext';
import { OnlyFansLists } from '../../types/onlyFansTypes';
import { parseOnlyFansListId, stripHtmlAndConvertParagraphs } from '../../utils/common';
import DialogAdaptive from './DialogAdaptive';
import DialogContentAdaptive from './DialogContentAdaptive';
import StyledCardTitleBar from './StyledCardTitleBar';
import StyledDialogActions from './StyledDialogActions';
import TextInputDialog from './TextInputDialog';

const LIST_REMOVE_TRANSITION_MS = 300;

const isDeletableList = (list: OnlyFansLists.List): list is OnlyFansLists.List & { id: number } => {
    const listId = parseOnlyFansListId(list.id);

    return list.canDelete && typeof listId === 'number';
};

const RecentDateSelectorDialog = (params: {
    open: boolean;
    onClose: () => void;
    onSubmit: (date: moment.Moment | null) => void;
    initialDate?: moment.Moment | null;
}) => {
    const { open, onClose, onSubmit, initialDate } = params;

    const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(initialDate || null);

    return (
        <DialogAdaptive open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <StyledCardTitleBar title="Recent Subscribers" />

            <DialogContentAdaptive>
                <Typography variant="body2" gutterBottom>
                    Select a date to target users who have subscribed after that date. This is useful for targeting recent subscribers. If
                    you want to target all subscribers, leave the date unset.
                </Typography>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en">
                    <CalendarPicker
                        date={selectedDate}
                        onChange={date => setSelectedDate(date)}
                        minDate={moment().subtract(1, 'month')}
                        maxDate={moment()}
                    />
                </LocalizationProvider>
            </DialogContentAdaptive>
            <StyledDialogActions>
                <Button onClick={() => onSubmit(null)} color="secondary">
                    Clear Date
                </Button>
                <Button
                    onClick={() => {
                        onSubmit(selectedDate);
                    }}
                    color="primary"
                >
                    Select
                </Button>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

type Props = {
    targetLists?: { id: number | string | OnlyFansLists.Type; name: string }[];
    setTargetLists?: (targetLists: { id: number | string | OnlyFansLists.Type; name: string }[]) => void;
    recentAfterDate?: moment.Moment | null;
    setRecentAfterDate?: (date: moment.Moment | null) => void;
    disabled?: boolean;
    disabledLists?: (number | string | OnlyFansLists.Type)[];
    listItemBackgroundColor?: string;
    showListIdCopyButton?: boolean;
    showSelectedListsChips?: boolean;
    hideBuiltInLists?: boolean;
    showDelete?: boolean;
    height?: number | string;
    sx?: SxProps<Theme>;
};

const OnlyFansListsMultiSelector = (props: Props) => {
    const {
        targetLists,
        setTargetLists,
        disabled,
        disabledLists,
        listItemBackgroundColor,
        height = 200,
        sx,
        recentAfterDate,
        setRecentAfterDate,
        showListIdCopyButton,
        showSelectedListsChips = false,
        hideBuiltInLists = false,
        showDelete,
    } = props;

    const userContext = useContext(UserContext);
    const dialog = useDialog();
    const canShowDelete = showDelete && userContext.isAdmin;

    const {
        // data: listsPages,
        lists,
        hasMore,
        loadedListsCount,
        isLoading: listsLoading,
        isFetching: listsFetching,
        refetch: refetchLists,
        deleteList,
        deletingListIds,
        removeListFromCache,
    } = useSubscriberLists(true);

    const [sortLists, setSortLists] = useState<boolean>(true);
    const [sortedLists, setSortedLists] = useState<OnlyFansLists.List[] | void | undefined>([]);
    const [removingListIds, setRemovingListIds] = useState<number[]>([]);

    const [bulkDeleteMode, setBulkDeleteMode] = useState<boolean>(false);
    const [bulkDeleteSelectedIds, setBulkDeleteSelectedIds] = useState<number[]>([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);
    const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ total: number; completed: number; currentName?: string } | null>(null);

    const [searchText, setSearchText] = useState<string>('');
    const [searchTextDialogOpen, setSearchTextDialogOpen] = useState<boolean>(false);

    const [recentDateSelectorOpen, setRecentDateSelectorOpen] = useState<boolean>(false);

    const deduplicateLists = useCallback(
        (lists: { id: number | string | OnlyFansLists.Type; name: string }[]) =>
            lists.filter((item, index, self) => index === self.findIndex(t => parseOnlyFansListId(t.id) === parseOnlyFansListId(item.id))),
        [],
    );

    // Memoize the sorting function
    const sortListsData = useCallback(
        (data: OnlyFansLists.List[]) => {
            if (!sortLists) return data;

            return [...data].sort((a, b) => {
                const x = a.name.toLowerCase();
                const y = b.name.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
            });
        },
        [sortLists],
    );

    // Handle list item selection
    const handleListItemClicked = useCallback(
        (id: number | OnlyFansLists.Type) => {
            if (!setTargetLists || !targetLists) return;

            const alreadyChecked = targetLists.find(item => parseOnlyFansListId(item.id) === parseOnlyFansListId(id));

            if (alreadyChecked) {
                setTargetLists(targetLists.filter(item => parseOnlyFansListId(item.id) !== parseOnlyFansListId(id)));
            } else if (lists) {
                const listName = lists.find(item => parseOnlyFansListId(item.id) === parseOnlyFansListId(id))?.name;

                if (listName) {
                    setTargetLists(deduplicateLists([...targetLists, { id, name: listName }]));
                }
            }
        },
        [lists, targetLists, setTargetLists],
    );

    const handleDeleteList = useCallback(
        (list: OnlyFansLists.List) => {
            const listId = parseOnlyFansListId(list.id);

            if (typeof listId !== 'number') {
                return;
            }

            dialog
                .confirm({
                    title: 'Delete list',
                    message: `Are you sure you want to delete "${stripHtmlAndConvertParagraphs(list.name)}"? This cannot be undone.`,
                })
                .then(async () => {
                    const success = await deleteList(listId);

                    if (!success) {
                        return;
                    }

                    if (targetLists && setTargetLists) {
                        setTargetLists(targetLists.filter(item => parseOnlyFansListId(item.id) !== listId));
                    }

                    setRemovingListIds(prev => (prev.includes(listId) ? prev : [...prev, listId]));

                    window.setTimeout(() => {
                        removeListFromCache(listId);
                        setRemovingListIds(prev => prev.filter(id => id !== listId));
                    }, LIST_REMOVE_TRANSITION_MS);
                })
                .catch(() => {});
        },
        [deleteList, dialog, removeListFromCache, setTargetLists, targetLists],
    );

    const handleBulkDeleteToggle = useCallback((listId: number) => {
        setBulkDeleteSelectedIds(prev => (prev.includes(listId) ? prev.filter(id => id !== listId) : [...prev, listId]));
    }, []);

    const handleBulkDelete = useCallback(() => {
        if (bulkDeleteSelectedIds.length === 0 || isBulkDeleting) {
            return;
        }

        const idsToDelete = [...bulkDeleteSelectedIds];

        dialog
            .confirm({
                title: 'Bulk delete lists',
                message: `Are you sure you want to delete ${idsToDelete.length} list(s)? This cannot be undone.`,
            })
            .then(async () => {
                setIsBulkDeleting(true);
                setBulkDeleteProgress({ total: idsToDelete.length, completed: 0 });

                let currentTargetLists = targetLists;

                for (let index = 0; index < idsToDelete.length; index++) {
                    const listId = idsToDelete[index];
                    const list = lists?.find(item => parseOnlyFansListId(item.id) === listId);

                    setBulkDeleteProgress({
                        total: idsToDelete.length,
                        completed: index,
                        currentName: list ? stripHtmlAndConvertParagraphs(list.name) : undefined,
                    });

                    const success = await deleteList(listId);

                    if (success) {
                        if (setTargetLists && currentTargetLists) {
                            currentTargetLists = currentTargetLists.filter(item => parseOnlyFansListId(item.id) !== listId);
                            setTargetLists(currentTargetLists);
                        }

                        removeListFromCache(listId);
                    }

                    setBulkDeleteProgress({
                        total: idsToDelete.length,
                        completed: index + 1,
                        currentName: list ? stripHtmlAndConvertParagraphs(list.name) : undefined,
                    });
                }

                setBulkDeleteSelectedIds([]);
                setIsBulkDeleting(false);
                setBulkDeleteProgress(null);
            })
            .catch(() => {});
    }, [bulkDeleteSelectedIds, deleteList, dialog, isBulkDeleting, lists, removeListFromCache, setTargetLists, targetLists]);

    const handleBulkDeleteModeToggle = useCallback(() => {
        setBulkDeleteMode(prev => {
            if (prev) {
                setBulkDeleteSelectedIds([]);
            }

            return !prev;
        });
    }, []);

    // Clear bulk selection when the search filter changes
    useEffect(() => {
        setBulkDeleteSelectedIds([]);
    }, [searchText]);

    // Update sorted lists whenever lists or sort preference changes
    useEffect(() => {
        if (!lists) {
            setSortedLists([]);
            return;
        }

        const hideBuiltInListsIds = hideBuiltInLists
            ? lists.filter(
                  list =>
                      list.type === OnlyFansLists.Type.Custom ||
                      list.type === OnlyFansLists.Type.RebillOff ||
                      list.type === OnlyFansLists.Type.RebillOn,
              )
            : lists;

        const filteredLists = searchText
            ? hideBuiltInListsIds.filter(list => list.name.toLowerCase().includes(searchText.toLowerCase()))
            : hideBuiltInListsIds;

        const newSortedLists = sortListsData(filteredLists);
        setSortedLists(newSortedLists);
    }, [lists, searchText, sortListsData]);

    const isLoading = listsLoading || listsFetching;

    const Row = ({ index, style }: ListChildComponentProps) => {
        const list = sortedLists ? sortedLists[index] : null;
        if (!list) return null;

        const listId = parseOnlyFansListId(list.id);
        const isDeleting = typeof listId === 'number' && deletingListIds.includes(listId);
        const isRemoving = typeof listId === 'number' && removingListIds.includes(listId);
        const isDisabled = disabledLists && disabledLists.includes(listId);
        const showBulkCheckbox = bulkDeleteMode && canShowDelete && isDeletableList(list);
        const isBulkSelected = typeof listId === 'number' && bulkDeleteSelectedIds.includes(listId);

        return (
            <Box
                style={style}
                sx={{
                    overflow: 'hidden',
                    opacity: isRemoving ? 0 : 1,
                    transform: isRemoving ? 'translateX(16px) scale(0.96)' : 'translateX(0) scale(1)',
                    transformOrigin: 'left center',
                    transition: theme =>
                        theme.transitions.create(['opacity', 'transform'], {
                            duration: LIST_REMOVE_TRANSITION_MS,
                            easing: theme.transitions.easing.easeInOut,
                        }),
                }}
            >
                <ListItem
                    key={list.id}
                    disablePadding
                    secondaryAction={
                        <Stack direction="row" alignItems="center">
                            {canShowDelete &&
                                !bulkDeleteMode &&
                                isDeletableList(list) &&
                                (isDeleting ? (
                                    <CircularProgress size={20} color="error" sx={{ mr: 1 }} />
                                ) : (
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        disabled={disabled || isDeleting || isRemoving || isBulkDeleting}
                                        onClick={event => {
                                            event.stopPropagation();
                                            handleDeleteList(list);
                                        }}
                                    >
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                ))}
                            {list.id === OnlyFansLists.Type.Recent && setRecentAfterDate ? (
                                <IconButton
                                    edge="end"
                                    aria-label="calendar"
                                    disabled={isDeleting || isRemoving}
                                    onClick={() => {
                                        setRecentDateSelectorOpen(true);
                                    }}
                                >
                                    <CalendarMonthIcon color={recentAfterDate ? 'primary' : 'disabled'} />
                                </IconButton>
                            ) : showListIdCopyButton ? (
                                <IconButton
                                    edge="end"
                                    aria-label="copy"
                                    disabled={isDeleting || isRemoving}
                                    onClick={() => {
                                        navigator.clipboard.writeText(list.id.toString());
                                    }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            ) : null}
                        </Stack>
                    }
                    sx={{
                        position: 'relative',
                        backgroundColor:
                            targetLists && targetLists.find(item => parseOnlyFansListId(item.id) === listId)
                                ? listItemBackgroundColor || 'text.gray.200'
                                : 'transparent',
                        opacity: isDeleting ? 0.55 : 1,
                        transition: theme => theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
                    }}
                >
                    {isDeleting && (
                        <LinearProgress
                            color="error"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 2,
                            }}
                        />
                    )}
                    <ListItemButton
                        disabled={disabled || isDisabled || isDeleting || isRemoving || isBulkDeleting}
                        role={undefined}
                        disableRipple={(!setTargetLists || !targetLists) && !showBulkCheckbox ? true : false}
                        dense
                        onClick={() => {
                            if (showBulkCheckbox && typeof listId === 'number') {
                                handleBulkDeleteToggle(listId);
                                return;
                            }

                            if (list.id === OnlyFansLists.Type.Recent && setRecentAfterDate) {
                                setRecentDateSelectorOpen(true);
                            } else {
                                handleListItemClicked(list.id);
                            }
                        }}
                    >
                        {showBulkCheckbox && (
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox
                                    id={`bulk-delete-checkbox-${list.id}`}
                                    name={`bulk-delete-checkbox-${list.id}`}
                                    tabIndex={-1}
                                    disableRipple
                                    disabled={isDeleting || isRemoving || isBulkDeleting}
                                    checked={isBulkSelected}
                                    onClick={event => event.stopPropagation()}
                                    onChange={() => {
                                        if (typeof listId === 'number') {
                                            handleBulkDeleteToggle(listId);
                                        }
                                    }}
                                    sx={{
                                        pl: 0,
                                        pr: 0,
                                    }}
                                />
                            </ListItemIcon>
                        )}
                        {!bulkDeleteMode && targetLists && setTargetLists && (
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox
                                    id={`checkbox-${list.id}`}
                                    name={`checkbox-${list.id}`}
                                    tabIndex={-1}
                                    disableRipple
                                    disabled={isDeleting || isRemoving}
                                    checked={
                                        targetLists && targetLists.find(item => parseOnlyFansListId(item.id) === listId) ? true : false
                                    }
                                    sx={{
                                        pl: 0,
                                        pr: 0,
                                    }}
                                />
                            </ListItemIcon>
                        )}
                        <ListItemText
                            primary={
                                list.id === OnlyFansLists.Type.Recent && setRecentAfterDate
                                    ? `Recent - last ${recentAfterDate ? `${moment().diff(recentAfterDate, 'days')} day(s)` : '24 hours'}`
                                    : stripHtmlAndConvertParagraphs(list.name)
                            }
                            secondary={isDeleting ? 'Deleting...' : `Users: ${(list.usersCount as number).toLocaleString()}`}
                        />
                    </ListItemButton>
                </ListItem>
            </Box>
        );
    };

    return (
        <Grid container spacing={0} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
            <Grid item xs>
                {bulkDeleteMode && canShowDelete ? (
                    <Chip
                        disabled={disabled || isBulkDeleting}
                        color={bulkDeleteSelectedIds.length === 0 ? 'default' : 'error'}
                        icon={<DeleteSweepIcon />}
                        label={`${bulkDeleteSelectedIds.length} to delete`}
                    />
                ) : (
                    targetLists &&
                    setTargetLists && (
                        <Chip
                            disabled={disabled}
                            color={!targetLists || targetLists.length === 0 ? 'error' : 'primary'}
                            icon={<CheckCircleIcon />}
                            label={`${targetLists ? targetLists.length : 0} selected`}
                        />
                    )
                )}
            </Grid>
            <Grid item xs={'auto'}>
                <Tooltip title="Search">
                    <span>
                        <IconButton
                            disabled={disabled}
                            onClick={() => {
                                setSearchTextDialogOpen(true);
                            }}
                        >
                            <SearchIcon fontSize="large" color={searchText ? 'primary' : 'disabled'} />
                        </IconButton>
                    </span>
                </Tooltip>
                {searchTextDialogOpen && (
                    <TextInputDialog
                        open={searchTextDialogOpen}
                        onClose={() => setSearchTextDialogOpen(false)}
                        onSubmit={value => {
                            setSearchText(value.trim());
                            setSearchTextDialogOpen(false);
                        }}
                        title="Search"
                        contentText="Search for lists by name"
                        label="Search"
                        placeholder="Search"
                        initialValue={searchText}
                        submitButtonText="Search"
                        clearButton={true}
                    />
                )}
            </Grid>
            {targetLists && setTargetLists && (
                <Grid item xs={'auto'}>
                    <Tooltip title="Clear Selection">
                        <span>
                            <IconButton
                                disabled={disabled}
                                onClick={() => {
                                    if (setTargetLists) {
                                        setTargetLists([]);
                                    }
                                }}
                            >
                                <ClearIcon fontSize="large" color={targetLists && targetLists.length > 0 ? 'error' : 'disabled'} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Grid>
            )}
            <Grid item xs={'auto'}>
                <Tooltip title="Sort lists alphabetically or custom like on OnlyFans">
                    <span>
                        <IconButton
                            disabled={disabled}
                            color={sortLists ? 'primary' : 'secondary'}
                            onClick={() => {
                                setSortLists(!sortLists);
                            }}
                        >
                            <SortByAlphaIcon fontSize="large" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid>
            <Grid item xs={'auto'}>
                <Tooltip title="Reload Lists from OnlyFans">
                    <span>
                        <IconButton
                            disabled={disabled || isBulkDeleting}
                            color="secondary"
                            onClick={() => {
                                refetchLists();
                            }}
                        >
                            <RefreshIcon fontSize="large" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid>
            {canShowDelete && (
                <Grid item xs={'auto'}>
                    <Tooltip title={bulkDeleteMode ? 'Exit bulk delete mode' : 'Bulk delete mode'}>
                        <span>
                            <IconButton
                                disabled={disabled || isBulkDeleting}
                                color={bulkDeleteMode ? 'error' : 'secondary'}
                                onClick={handleBulkDeleteModeToggle}
                            >
                                <DeleteSweepIcon fontSize="large" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Grid>
            )}
            {bulkDeleteMode && canShowDelete && (
                <Grid item xs={'auto'}>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        disabled={disabled || isBulkDeleting || bulkDeleteSelectedIds.length === 0}
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                    >
                        Delete selected
                    </Button>
                </Grid>
            )}
            <Grid item xs={12}>
                {bulkDeleteProgress && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            {bulkDeleteProgress.completed < bulkDeleteProgress.total
                                ? `Deleting ${bulkDeleteProgress.currentName ? `"${bulkDeleteProgress.currentName}"` : 'list'} (${
                                      bulkDeleteProgress.completed + 1
                                  } of ${bulkDeleteProgress.total})...`
                                : `Deleted ${bulkDeleteProgress.total} list(s)`}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            color="error"
                            value={(bulkDeleteProgress.completed / bulkDeleteProgress.total) * 100}
                        />
                    </Box>
                )}
                {(isLoading || hasMore) && <LinearProgress />}
                {sortedLists && typeof sortedLists !== 'undefined' ? (
                    <Paper variant="outlined" sx={{ height, overflow: 'auto', ...sx }}>
                        <Stack direction="column">
                            <FixedSizeList
                                height={isLoading || hasMore ? (typeof height === 'number' ? height - 4 : 196) : height}
                                width="100%"
                                itemSize={57}
                                itemCount={sortedLists.length}
                                itemData={sortedLists}
                            >
                                {Row}
                            </FixedSizeList>
                        </Stack>
                        <RecentDateSelectorDialog
                            open={recentDateSelectorOpen}
                            onClose={() => setRecentDateSelectorOpen(false)}
                            onSubmit={date => {
                                if (!targetLists || !setTargetLists) return;

                                if (setRecentAfterDate) {
                                    setRecentAfterDate(date);

                                    if (date) {
                                        const recentListIndex = targetLists.findIndex(item => item.id === OnlyFansLists.Type.Recent);

                                        if (recentListIndex === -1) {
                                            const recentListName =
                                                lists?.find(list => list.id === OnlyFansLists.Type.Recent)?.name || 'Recent Subscribers';

                                            setTargetLists(
                                                deduplicateLists([...targetLists, { id: OnlyFansLists.Type.Recent, name: recentListName }]),
                                            );
                                        }
                                    } else {
                                        setTargetLists(targetLists.filter(item => item.id !== OnlyFansLists.Type.Recent));
                                    }
                                }
                                setRecentDateSelectorOpen(false);
                            }}
                            initialDate={recentAfterDate || undefined}
                        />
                    </Paper>
                ) : (
                    <Alert severity="error">Could not load lists!</Alert>
                )}
                {showSelectedListsChips && targetLists && targetLists.length > 0 && (
                    <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        {targetLists.map(list => {
                            const foundList = lists?.find(l => parseOnlyFansListId(l.id) === parseOnlyFansListId(list.id));

                            if (isLoading && !foundList) {
                                return <Skeleton key={list.id} variant="rectangular" width="100px" height="24px" animation="wave" />;
                            }

                            return (
                                <Chip
                                    key={list.id}
                                    label={foundList ? foundList.name : list.name}
                                    size="small"
                                    color={!isLoading && !foundList ? 'warning' : 'default'}
                                    onDelete={
                                        setTargetLists && !disabled
                                            ? () =>
                                                  setTargetLists(
                                                      targetLists.filter(
                                                          item => parseOnlyFansListId(item.id) !== parseOnlyFansListId(list.id),
                                                      ),
                                                  )
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </Stack>
                )}
            </Grid>
        </Grid>
    );
};

export default OnlyFansListsMultiSelector;
